/**
 * LocationClassificationNote.tsx — the block + block-group classification readout (T-512, T-498).
 *
 * Noreen asked to READ a location's classification and score in the stats panel, not infer it from
 * a colour on the map. T-498 makes it a FIRST-CLASS element rather than a buried gray sentence: the
 * headline class (corridor/retail, or the residential block-group class it inherits) is featured in
 * a prominent badge, with the two honest supporting sentences beneath it.
 *
 * It is a pure presentational component — the containing block/BG are resolved upstream
 * (selectLocationClassification) and the honest wording (which class is the headline, the two
 * sentences) is shared with the PDF one-pager via locationClassification.ts, so both surfaces state
 * the same thing. Every "absent" is still its own honest fact: no coverage vs scored-but-neither vs
 * still-loading vs failed are four different facts, a missing score reads "unavailable" (never a
 * fabricated 0), and when NEITHER a block nor a block group is classified there is no badge at all —
 * prominence must not pressure missing data into looking populated.
 */
import { MapPin } from 'lucide-react'
import {
  classificationPrimary,
  classificationSentences,
  type LocationClassification,
} from '@/lib/locationClassification'

export interface LocationClassificationNoteProps {
  /** loading → lookup in flight; error → the vicinity fetch failed; ready → `classification` is set. */
  status: 'loading' | 'error' | 'ready'
  classification: LocationClassification | null
}

export function LocationClassificationNote({
  status,
  classification,
}: LocationClassificationNoteProps) {
  // Loading / failed states: an honest one-liner, no badge, no invented class.
  if (status === 'loading') {
    return <Shell body="Looking up this location's block classification…" />
  }
  if (status === 'error' || !classification) {
    return <Shell body="Block classification is unavailable — the lookup did not complete." />
  }

  const primary = classificationPrimary(classification)
  const sentences = classificationSentences(classification)

  return (
    <div
      data-testid="location-classification"
      className="mt-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5"
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <MapPin size={13} aria-hidden="true" className="flex-shrink-0" />
        Block location classification
      </div>

      {/* The headline class as a prominent badge — the first-class visual element (T-498). Rendered
          only when a class exists; when neither block nor BG is classified the sentences below carry
          the honest explanation and no badge is drawn. The badge is a neutral brand-tinted chip: the
          block-group residential shares (hcc/hco) have no defensible good/bad banding, so prominence
          comes from size + weight + placement, never a score colour that would imply a verdict. */}
      {primary && (
        <div
          data-testid="classification-badge"
          className="mt-1.5 inline-flex items-baseline gap-2 rounded-md bg-primary/10 px-2.5 py-1"
        >
          <span className="text-base font-bold leading-none text-foreground">{primary.label}</span>
          {/* T-616: a class can have no score to state (Non-Residential is named BY the absence of
              residential input). The clause is omitted entirely rather than printing "unavailable",
              which would imply a number that ought to have arrived. */}
          {primary.scoreLabel && (
            <span className="text-xs text-muted-foreground">
              {primary.scoreLabel}{' '}
              <span className="font-semibold text-foreground">{primary.scoreText}</span>
            </span>
          )}
        </div>
      )}

      {/* The two honest supporting sentences (shared wording with the PDF). `text-foreground` for the
          key nouns keeps them legible in both themes; the prose is muted. */}
      <p className="mt-1.5 text-xs text-muted-foreground">
        {sentences.block} {sentences.blockGroup}
      </p>
    </div>
  )
}

/** The loading / unavailable shell — same container + eyebrow, a single honest line, no badge. */
function Shell({ body }: { body: string }) {
  return (
    <div
      data-testid="location-classification"
      className="mt-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5"
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <MapPin size={13} aria-hidden="true" className="flex-shrink-0" />
        Block location classification
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">{body}</p>
    </div>
  )
}
